/**
 * Three marks and two measures, which is the whole of what the phase-2 note owes
 * (`docs/notes/2026-08-20-viewer-canvas.md` §10).
 *
 * The note's budget — *"first tile painted ≤ 400 ms desktop, ≤ 1.2 s tablet-4G; fit-to-screen
 * sharp ≤ 800 ms / ≤ 2.5 s, p75 on a cold cache, measured from route mount"* — is **derived**,
 * built from a table of round trips and throughputs. It says so, and it says what has to replace
 * it: *"three `performance.mark()`s and two measures between them, taken on a real A0 render
 * through the real pipeline. If it misses the budget, the budget moves and the note records why;
 * what is not acceptable is shipping phase 3 without anybody having looked."*
 *
 * **What these are not**, in the note's own words: *"a metrics service, a dashboard, or a
 * per-session beacon. They are a local measurement, run deliberately."* Nothing here reports
 * anywhere. The marks sit in the browser's own performance timeline, where a test — or a person
 * with devtools open — can read them.
 *
 * **Why the marks live in a module rather than inline.** The three points are in two components:
 * mount is the route's, the two paint marks are the canvas's. Naming them in one place is what
 * makes `viewer.first-tile-painted` mean the same instant to the person reading the timeline as
 * to the code writing it, and it is where "once per opened sheet" is enforced — OpenSeadragon
 * fires `tile-drawn` per tile per frame, so an unguarded mark would be a few hundred marks and
 * a measure of the last one.
 */

export const MOUNT = 'viewer.mount';
export const FIRST_TILE = 'viewer.first-tile-painted';
export const FIT_SHARP = 'viewer.fit-sharp';

/** `viewer.time-to-first-tile` and `viewer.time-to-fit-sharp`, the two the budget is about. */
export const TO_FIRST_TILE = 'viewer.time-to-first-tile';
export const TO_FIT_SHARP = 'viewer.time-to-fit-sharp';

/**
 * Which sheet the current marks belong to.
 *
 * Opening a second drawing without leaving the screen has to start the measurement again, or
 * the second sheet's first tile would be measured from the first sheet's mount — a number that
 * looks like a catastrophic regression and is an artefact of the clock never being restarted.
 */
let openedFor: string | null = null;

function reached(name: string): boolean {
  return performance.getEntriesByName(name, 'mark').length > 0;
}

function measure(name: string, end: string): void {
  // Guarded rather than trusted: a `measure` whose start mark is missing throws, and a viewer
  // that threw because a timing mark was absent would be the measurement breaking the thing it
  // is measuring.
  if (!reached(MOUNT) || !reached(end)) return;
  performance.measure(name, MOUNT, end);
}

/** The route mounted for `sheetId`. Clears the previous sheet's marks. */
export function markMount(sheetId: string): void {
  if (openedFor === sheetId) return;
  openedFor = sheetId;
  for (const name of [MOUNT, FIRST_TILE, FIT_SHARP]) performance.clearMarks(name);
  for (const name of [TO_FIRST_TILE, TO_FIT_SHARP]) performance.clearMeasures(name);
  performance.mark(MOUNT);
}

/** The first tile of this sheet reached the screen. Fires once; `tile-drawn` does not. */
export function markFirstTile(): void {
  if (reached(FIRST_TILE)) return;
  performance.mark(FIRST_TILE);
  measure(TO_FIRST_TILE, FIRST_TILE);
}

/** Every tile the current view needs has painted — the sheet is sharp rather than blurred. */
export function markFitSharp(): void {
  if (reached(FIT_SHARP)) return;
  performance.mark(FIT_SHARP);
  measure(TO_FIT_SHARP, FIT_SHARP);
}
